import * as Sentry from '@sentry/react';
import { toast } from 'sonner';

// Base API configuration - connects to our backend
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3005/api/v1';

// Warn if using HTTP in non-development environment
if (API_BASE_URL.startsWith('http://') && !API_BASE_URL.includes('localhost')) {
  console.error(
    '[SECURITY] API_BASE_URL is using HTTP in a non-localhost environment. Set VITE_API_URL to an HTTPS URL.',
  );
}

// Types for API responses
interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  timestamp: string;
}

// Dashboard types - Updated to match backend controller
interface DashboardStats {
  totalRevenue: {
    amount: number;
    trend: 'up' | 'down' | 'stable';
    description?: string;
  };
  activeCases: {
    count: number;
    trend: 'up' | 'down' | 'stable';
    description?: string;
  };
  pendingInvoices: {
    count: number;
    trend: 'up' | 'down' | 'stable';
    amount?: number;
    description?: string;
  };
  completionRate: {
    percentage: number;
    trend: 'up' | 'down' | 'stable';
    description?: string;
  };
}

interface PaymentData {
  month: string;
  amount: number;
}

interface RecentRequest {
  id: string;
  plaintiff: string;
  attorney: string;
  status: 'New' | 'Conflict' | 'Cleared' | 'Pending';
  date: string;
  type: string;
}

interface RecentActivity {
  action: string;
  case: string;
  time: string;
  caseId?: string; // Deep-link target read by dashboard.tsx activity rows
}

// Attorney Dashboard types
interface AttorneyDashboardStats {
  totalExpenses: {
    amount: number;
    trend: 'up' | 'down' | 'stable';
    description?: string;
  };
  activeCases: {
    count: number;
    trend: 'up' | 'down' | 'stable';
    description?: string;
  };
  outstandingInvoices: {
    count: number;
    trend: 'up' | 'down' | 'stable';
    amount?: number;
    description?: string;
  };
  deliverableFulfillment: {
    percentage: number;
    trend: 'up' | 'down' | 'stable';
    description?: string;
  };
}

interface AttorneyActivity {
  id: string;
  action: string;
  caseName: string;
  caseId: string;
  time: string;
  type: string;
}

interface AttorneyInvoice {
  id: string;
  invoiceNumber: string;
  caseId: string;
  caseName: string;
  doctorName: string;
  amount: number;
  subtotal: number;
  tax: number;
  status: string;
  dueDate: string | null;
  paidDate: string | null;
  createdAt: string;
  title: string | null;
  notes?: string | null;
  clientName?: string | null;
  clientEmail?: string | null;
  description?: string | null;
  billableItems?: any;
  sentAt?: string | null;
  date?: string | null;
  doctorId?: string;
  ownerRole?: string;
  paymentUrl?: string | null;
}

interface AttorneyInvoicesResponse {
  invoices: AttorneyInvoice[];
  summary: {
    total: number;
    paid: number;
    pending: number;
    overdue: number;
  };
}

interface DeliverableRequestData {
  type: string;
  title: string;
  description?: string;
  urgency: string;
  dateNeeded?: string;
  notes?: string;
  expertId?: string;
  externalExpertEmail?: string;
}

interface ExpertSearchResult {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  title: string | null;
  specialty: string | null;
  subspecialty: string | null;
  available: boolean;
  bio: string | null;
}

// DashboardNotification is declared below (line ~585) with export

interface AttorneyProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  title: string | null;
  firmName: string | null;
  billingAddress: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  } | null;
}

// Attorney client intake types
interface AttorneyPublicProfile {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  title: string;
  suffix: string;
  vanityUrl: string;
}

interface ClientIntakeData {
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  patientDateOfBirth?: string;
  caseType: string;
  description: string;
  urgency?: string;
  dateNeeded?: string;
  notes?: string;
  encryptedPayload?: string | null;
}

interface AttorneyIntakeRequest {
  id: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  patientDateOfBirth?: string | null;
  caseType: string;
  urgency: string;
  status: string;
  description: string;
  notes: string;
  dateSubmitted: string;
  dateNeeded: string | null;
  responseNote: string | null;
  caseId: string | null;
  isEncrypted?: boolean;
}

interface AttorneyIntakeRequestsResponse {
  requests: AttorneyIntakeRequest[];
  total: number;
}

// Pre-screening request types
interface PreScreeningRequest {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  requestDetails: string;
  dateSubmitted: string;
  status: 'pending' | 'accepted' | 'rejected' | 'conflict';
  documentUrl?: string;
  documentUrlType?: 'sharepoint' | 'dropbox' | 'other';
  // Plaintext attorney info added in #887 (consumers in dashboard.tsx,
  // header.tsx, pre-screening-requests.tsx already read these fields).
  attorneyName?: string;
  attorneyEmail?: string;
  firmName?: string;
  // Intake-request fields returned by /intake-requests and read by
  // dashboard.tsx, header.tsx, and pre-screening-requests.tsx.
  requestNumber?: number | null; // Per-doctor request number (e.g., Request #1, #2, #3)
  plaintiff?: string;
  notes?: string;
  caseType?: string;
  estimatedHours?: number;
  urgency?: string;
  responseNote?: string;
  vanitySlug?: string;
  userId?: string;
  encryptedPayload?: string; // Encrypted sensitive data
  // Appointment inquiry fields
  appointmentType?: string;
  preferredDate?: string;
  patientPhone?: string;
  patientEmail?: string;
  specialInstructions?: string;
  patientDateOfBirth?: string;
  caseId?: string | null;
  createdCaseId?: string | null;
}

// Case types
interface CaseRecord {
  id: string;
  doctorId?: string; // Owning doctor user id (returned by getCaseById)
  caseNumber?: number | null; // Per-doctor case number (e.g., Case #1, #2, #3)
  caseTitle?: string; // Encrypted CASEKEY: envelope; decrypted by consumer
  plaintiff?: string; // Full plaintiff name
  firstName: string; // Plaintiff first name (for backward compatibility)
  lastName: string; // Plaintiff last name (for backward compatibility)
  email: string;
  company: string;
  requestDetails: string;
  description?: string; // Encrypted top-level case description
  documentUrl?: string; // Encrypted top-level case document URL
  dateSubmitted: string;
  dateAccepted: string;
  status: 'active' | 'completed' | 'on-hold';
  totalRevenue: number;
  invoiceCount: number;
  fileCount: number;
  deliverableCount?: number;
  // Attorney appointment list returned alongside the case detail (used by
  // case-record-detail.tsx to render the scheduling panel).
  appointments?: Array<{
    id: string;
    appointmentType?: string;
    scheduledAt?: string;
    duration?: number;
    status?: string;
    [key: string]: unknown;
  }>;
  deliverableStats?: {
    total: number;
    pending: number;
    inProgress: number;
    completed: number;
    urgent: number;
  };
  hasActiveDeliverables?: boolean;
  caseType?: string;
  urgency?: string;
  attorneyName?: string;
  attorneyEmail?: string;
  firmName?: string;
  intakeRequests?: Array<{
    id: string;
    caseType: string;
    specialty: string;
    status: string;
    dateSubmitted: string;
    plaintiff?: string;
    description?: string;
    documentUrl?: string;
    notes?: string;
    // Appointment-inquiry fields (encrypted CASEKEY: envelope on the wire,
    // decrypted by consumers like case-record-detail.tsx)
    appointmentType?: string;
    preferredDate?: string;
    patientPhone?: string;
    patientEmail?: string;
    patientDateOfBirth?: string;
    specialInstructions?: string;
    files?: Array<{
      id: string;
      fileName: string;
      originalName?: string;
      fileSize?: number;
      mimeType?: string;
      category?: string;
      createdAt: string;
    }>;
  }>;
  deliverables?: Array<{
    id: string;
    caseId: string;
    intakeRequestId: string;
    type: string;
    title: string;
    description?: string;
    status: string;
    priority: string;
    dateRequested?: string;
    dateCompleted?: string;
    notes?: string;
    createdAt: string;
    updatedAt: string;
    typeLabel?: string;
    statusLabel?: string;
    isActive?: boolean;
    intakeRequest?: {
      id: string;
      caseType: string;
      specialty: string;
      plaintiff?: string;
      description?: string;
    };
  }>;
}

/**
 * Backend case-status enum values (wire format).
 *
 * The Prisma model uses uppercase (`ACTIVE`, `COMPLETED`, `CANCELLED`,
 * `SCREENING`, `ARCHIVED`). The legacy `CaseRecord.status` type uses
 * lowercase display values (`active`/`completed`/`on-hold`) which is what
 * the UI renders — a translation map sits between the wire format and the
 * display. Until that legacy type is migrated, use this enum at the API
 * boundary so `apiClient.updateCase()` accepts the wire format directly.
 */
export type BackendCaseStatus = 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'SCREENING' | 'ARCHIVED';

/**
 * Body of `PUT /api/v1/cases/:id`. Extends `Partial<CaseRecord>` but
 * overrides `status` with the backend wire-format enum so consumers
 * can send the uppercase value the API expects.
 */
export type UpdateCasePayload = Omit<Partial<CaseRecord>, 'status'> & {
  status?: BackendCaseStatus;
};

// Deliverable types
interface Deliverable {
  id: string;
  caseId: string;
  type: string;
  title: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  description: string;
  assignedExpertId?: string;
  dueDate?: string;
  createdAt: string;
  updatedAt: string;
}

// Case Collaborator types
export interface CaseCollaborator {
  id: string;
  email: string;
  role: 'ATTORNEY' | 'PARALEGAL';
  addedAt: string;
  isPrimary: boolean;
  linked: boolean;
  userName: string | null;
}

// Service types
interface Service {
  id: string;
  title: string;
  description: string;
  price: number;
  active: boolean;
  category: string;
}

// Invoice types
interface Invoice {
  id: string;
  invoiceNumber: string;
  caseId: string;
  caseName: string;
  clientName: string;
  clientEmail: string;
  services?: {
    serviceId: string;
    quantity: number;
    title: string;
    price: number;
    description?: string; // Custom line-item description (invoices.tsx detail view)
    unitPrice?: number; // Per-unit price for custom line items without a saved service
  }[];
  subtotal: number;
  tax: number;
  totalAmount: number;
  /** Sum of settled payments; absent on responses that predate payment aggregates. */
  amountPaid?: number;
  /** Outstanding amount after settled payments. */
  balanceDue?: number;
  date: string;
  dueDate: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  paidAt?: string;
  createdAt: string;
  lastReminderSentAt?: string | null;
  isEncrypted?: boolean; // Flag to track if invoice data was encrypted (for send validation)
  title?: string;
  description?: string;
  notes?: string;
  taxRate?: number;
  case?: { caseTitle: string; attorneyName: string; attorneyEmail: string };
  doctor?: { firstName: string; lastName: string; email: string; title: string; specialty: string };
}

// Payment types
enum PaymentMethod {
  CARD = 'CARD',
  CHECK = 'CHECK',
  ACH = 'ACH',
  WIRE = 'WIRE',
  OTHER = 'OTHER',
}

enum PaymentStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED',
  CANCELLED = 'CANCELLED',
}

enum CheckStatus {
  RECEIVED = 'RECEIVED',
  DEPOSITED = 'DEPOSITED',
  CLEARED = 'CLEARED',
  BOUNCED = 'BOUNCED',
}

interface Payment {
  id: string;
  invoiceId: string;
  doctorId: string;
  amount: number;
  paymentMethod: PaymentMethod;
  status: PaymentStatus;

  // Stripe payment fields
  stripePaymentIntentId?: string;
  stripeChargeId?: string;

  // Check payment fields
  checkNumber?: string;
  checkDate?: string;
  checkBankName?: string;
  checkImageUrl?: string;
  checkStatus?: CheckStatus;

  // Metadata
  recordedBy?: string;
  notes?: string;
  paymentDate: string;
  createdAt: string;
  updatedAt: string;
}

interface RecordPaymentRequest {
  amount: number;
  paymentMethod: PaymentMethod;
  checkNumber?: string;
  checkDate?: string;
  checkBankName?: string;
  checkImageUrl?: string;
  checkStatus?: CheckStatus;
  notes?: string;
  // Frontend-decrypted values for email notification (end-to-end encryption)
  recipientEmail?: string;
  recipientName?: string;
  decryptedCaseName?: string;
}

/** Result of recording a manual payment. */
interface RecordPaymentResult {
  payment: Payment;
  invoice: Invoice;
  amountPaid: number;
  balanceDue: number;
  isFullyPaid: boolean;
}

interface UpdateCheckStatusRequest {
  checkStatus: CheckStatus;
  notes?: string;
}

// Settings types
interface ProfileSettings {
  name: string;
  title: string;
  middleInitial?: string;
  specialty: string;
  subspecialty?: string;
  suffix?: string;
  email: string;
  phone: string;
  phoneNumber?: string;
  // Server may return either a formatted string or a structured billing address
  address?:
    | string
    | {
        street: string;
        city: string;
        state: string;
        zipCode: string;
        country: string;
      };
  appointmentInstructions?: string;
  billingAddress?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  bio: string;
  credentials: string[];
  vanityUrl: string;
  vanityUrlActive: boolean;
  statesPracticedIn?: string[];
  federalCourtTestified?: boolean;
  platformFeeDiscount?: number; // 0-100, percentage discount on 2% platform fee
  id?: string;
  clerkId?: string;
  role?: string;
  hourlyRate?: number;
  availability?: boolean;
  encryptionEnabled?: boolean;
  trialStartedAt?: string;
  trialEndsAt?: string;
  trialUsed?: boolean;
  referralCode?: string;
  subscriptionTier?: string;
  subscriptionStatus?: string;
  emailVerified?: boolean;
  createdAt?: string;
  updatedAt?: string;
  teamOwnerId?: string;
  teamMemberRole?: string;
  doctorFirstName?: string;
  doctorLastName?: string;
}

// Public doctor profile — returned by public-facing endpoints (subset of ProfileSettings)
export interface PublicDoctorProfile {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  suffix?: string;
  specialty?: string;
  subspecialty?: string;
  bio?: string;
  vanityUrl?: string;
  hourlyRate?: number;
  availability?: string;
}

// Admin types for discount management
interface AdminDoctor {
  id: string;
  clerkId: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  specialty: string | null;
  /** LEGACY 2%-fee discount — dormant post-#1038 but retained for revert. */
  platformFeeDiscount: number;
  /** Live subscription-discount percent-off on the $150/mo plan (1-99). */
  subscriptionDiscountPercentOff: number | null;
  subscriptionStatus: string | null;
  subscriptionTier: string | null;
  hasStripeAccount: boolean;
  stripeOnboardingComplete: boolean;
  createdAt: string;
}

interface ProductionMetrics {
  totalDoctors: number;
  doctorsWithDiscounts: number;
  totalDoctorRevenue: number;
  totalPlatformRevenue: number;
  totalRequestsProcessed: number;
  uniqueIntakeEmailCount: number;
  totalUsers: number;
  usersOverTime: Array<{ date: string; users: number }>;
  revenueOverTime: Array<{ date: string; revenue: number }>;
  dataSource: 'production' | 'local'; // Indicates which database was used
  // QA-SWARM #376: connection diagnostics emitted by adminController.getProductionMetrics
  connectionDiagnostics?: {
    productionConfigured: boolean;
    environment?: string;
    connectionError: string | null;
    isProductionData: boolean;
  };
}

interface EnvironmentHealthStatus {
  healthy: boolean;
  status: string;
  responseTime: number;
  error?: string;
  lastChecked: string;
}

interface EnvironmentHealth {
  localhost: EnvironmentHealthStatus;
  staging: EnvironmentHealthStatus;
  production: EnvironmentHealthStatus;
}

interface UpdateDoctorDiscountRequest {
  discount: number; // 0-100
}

// Attorney intake form types
interface AttorneyIntakeData {
  attorneyName: string;
  attorneyEmail: string;
  paralegalEmail?: string; // CC email recipient
  secondaryAttorneyEmail?: string; // CC email recipient
  firmName: string;
  plaintiffName: string;
  plaintiffEmail?: string; // Smart case matching field (optional)
  notes: string;
  // files: planned for direct-attach upload but the API (submitIntakeForm)
  // does not transmit files; the attorney-intake form has no file picker.
  // Keep the field declared (forward-compatible) but optional.
  files?: File[];
  documentUrl: string;
  documentUrlType: 'sharepoint' | 'dropbox' | 'other';
  caseType: string; // Case type from form
  urgency: string; // Urgency level from form
  // Appointment inquiry fields
  appointmentType?: string | null;
  preferredDate?: string | null;
  patientPhone?: string | null;
  patientEmail?: string | null;
  patientDateOfBirth?: string | null;
  specialInstructions?: string | null;
  encryptedPayload?: string; // Optional encrypted payload
}

// Case import types (Issue #614)
interface CaseImportData {
  attorneyName: string;
  attorneyEmail: string;
  attorneyPhone?: string;
  paralegalEmail?: string;
  secondaryAttorneyEmail?: string;
  firmName?: string;
  plaintiffName: string;
  plaintiffEmail?: string;
  caseTitle: string;
  caseType: string;
  specialty: string;
  urgency?: string;
  description?: string;
  estimatedHours?: number;
  dateNeeded?: string;
  notes?: string;
  documentUrl?: string;
  // Appointment inquiry fields
  appointmentType?: string;
  preferredDate?: string;
  patientPhone?: string;
  patientEmail?: string;
  patientDateOfBirth?: string;
  specialInstructions?: string;
  encryptedPayload?: string; // Optional encrypted payload
}

interface CaseImportResult {
  caseId: string;
  intakeRequestId: string;
  isNewCase: boolean;
  existingCaseId?: string;
  status: string;
  message: string;
}

interface DeliverableImportData {
  caseTitle: string;
  caseType: string;
  specialty: string;
  urgency?: string;
  description?: string;
  estimatedHours?: number;
  dateNeeded?: string;
  notes?: string;
  documentUrl?: string;
  // Appointment inquiry fields
  appointmentType?: string;
  preferredDate?: string;
  patientPhone?: string;
  patientEmail?: string;
  patientDateOfBirth?: string;
  specialInstructions?: string;
  encryptedPayload?: string; // Optional encrypted payload
}

interface DeliverableImportResult {
  deliverableId: string;
  caseId: string;
  status: string;
  message: string;
}

// Stripe Connect types
interface StripeAccountStatus {
  connected: boolean;
  accountId: string | null;
  status: {
    accountId: string;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    requirementsCurrentlyDue: string[];
    requirementsEventuallyDue: string[];
    requirementsPastDue: string[];
    currentlyDue: boolean;
    pastDue: boolean;
    disabled: boolean;
    disabledReason?: string;
  } | null;
}

interface StripeAccountCreated {
  accountId: string;
  status: string;
}

interface StripeOnboardingLink {
  url: string;
  expiresAt: number;
}

interface StripeDashboardLink {
  url: string;
}

interface StripeBalance {
  available: Array<{
    amount: number;
    currency: string;
  }>;
  pending: Array<{
    amount: number;
    currency: string;
  }>;
}

interface StripePayout {
  id: string;
  amount: number;
  currency: string;
  arrival_date: number;
  created: number;
  status: string;
  type: string;
}

// Notification types
export interface DashboardNotification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  data?: Record<string, unknown>;
  read: boolean;
  createdAt: string;
}

// Encryption types
export interface EncryptionStatus {
  enabled: boolean;
  hasSalt: boolean;
  encryptedRecordCount: number;
  setup: boolean;
  encryptedPrivateKey?: string;
}

interface EncryptionInitResponse {
  salt: string;
}

interface EncryptionSaltResponse {
  salt: string;
}

// Team member types
export interface TeamMember {
  id: string;
  email: string;
  role: 'ADMIN' | 'MEMBER';
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'REVOKED';
  invitedAt: string;
  acceptedAt?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  userId?: string | null;
  encryptionEnabled: boolean;
}

export interface TeamInfo {
  doctorId: string;
  doctorName: string;
  doctorEmail: string;
  memberCount: number;
  maxMembers: number;
  isTeamMember: boolean;
  teamMemberRole: 'ADMIN' | 'MEMBER' | null;
}

export interface ReferralStats {
  referralCode: string | null;
  totalEarnings: number;
  pendingEarnings: number;
  activeReferralCount: number;
  referrals: Array<{
    name: string;
    createdAt: string;
  }>;
}

export interface SubscriptionTier {
  id: string;
  name: string;
  maxMembers: number;
  priceMonthly: number;
  audience?: 'attorney' | 'doctor' | 'internal';
  trialDays?: number;
  /** `flat` = fixed monthly price; `metered` = percent-of-revenue billed monthly. */
  billing?: 'flat' | 'metered';
  /** Percent of platform revenue billed monthly (metered tiers only). */
  percentOfRevenue?: number;
}

/** Doctor-scoped subscription-gate state, mirrored from the backend
 * `DoctorSubscriptionState` in subscriptionService.ts. `allowed=false`
 * with reason `expired` or `never_subscribed` triggers the paywall. */
export interface DoctorSubscriptionState {
  allowed: boolean;
  reason:
    | 'admin'
    | 'not_doctor'
    | 'active'
    | 'trialing'
    | 'grace'
    | 'past_due'
    | 'expired'
    | 'never_subscribed';
  graceEndsAt?: string | null;
  currentPeriodEnd?: string | null;
  subscriptionStatus?: string | null;
  /** Tier ID backing the current subscription (doctor_monthly | doctor_percent). */
  tier?: string | null;
  /** Admin-granted percent-off on the $150/mo plan (1-99). Null = no discount. */
  discountPercentOff?: number | null;
}

export interface SubscriptionStatus {
  tier: string;
  status: string | null;
  currentPeriodEnd: string | null;
  /** Grandfather grace window end (doctor-only, null for others). */
  graceEndsAt: string | null;
  teamMemberCount: number;
  maxMembers: number;
  tiers: SubscriptionTier[];
  /** Doctor-scoped gate state — null for non-doctors. */
  doctor: DoctorSubscriptionState | null;
}

// Calendar integration types
type CalendarEntityType =
  | 'DELIVERABLE_DUE'
  | 'APPOINTMENT'
  | 'SCHEDULING_DEADLINE'
  | 'DOCUMENT_DEADLINE'
  | 'INVOICE_DUE';

interface CalendarPreferences {
  includeDeliverableDueDates: boolean;
  includeAppointments: boolean;
  includeSchedulingDeadlines: boolean;
  includeDocumentDeadlines: boolean;
  includeInvoiceDueDates: boolean;
  includePatientNames: boolean;
  reminderLeadDays: number[];
  timezone: string;
}

interface CalendarConnectionState {
  connected: boolean;
  preferences: CalendarPreferences;
  feedTokenIssuedAt?: string | null;
  lastAccessedAt?: string | null;
  /** Present only while connected. */
  httpsUrl?: string;
  webcalUrl?: string;
}

interface CalendarEventSummary {
  entityType: CalendarEntityType;
  entityId: string;
  caseId: string;
  title: string;
  start: string;
  end: string | null;
  allDay: boolean;
  location: string | null;
}

class ApiClient {
  private baseUrl: string;
  private onUnauthorized?: () => void;
  private getTokenFn?: () => Promise<string | null>;
  private tokenOverride: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  // Set callback for handling 401 errors
  setUnauthorizedCallback(callback: () => void) {
    this.onUnauthorized = callback;
  }

  // Set token getter function (for Clerk integration)
  setTokenGetter(getter: () => Promise<string | null>) {
    this.getTokenFn = getter;
  }

  // Set a static token that takes precedence over the token getter.
  // Used by magic-link flows (e.g. AttorneyDashboard JWT) where Clerk auth is unavailable.
  setTokenOverride(token: string | null) {
    this.tokenOverride = token;
  }

  // Public method to get auth token (for components that need direct fetch calls)
  async getToken(): Promise<string | null> {
    if (this.tokenOverride) {
      return this.tokenOverride;
    }
    if (this.getTokenFn) {
      return await this.getTokenFn();
    }
    return localStorage.getItem('authToken');
  }

  // Helper to get auth headers
  private async getHeaders(): Promise<HeadersInit> {
    const token = await this.getToken();

    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // Helper to check if error is transient (temporary server issue)
  private isTransientError(error: unknown): boolean {
    const status = (error as any)?.status;
    const isNetworkError = error instanceof TypeError;
    const isTransientStatus = typeof status === 'number' && [502, 504, 520].includes(status);

    return isNetworkError || isTransientStatus;
  }

  // Generic request handler with improved error handling and user messaging
  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requestConfig?: { silent?: boolean },
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Get auth token: override (magic link JWT) > Clerk > localStorage
    const token = await this.getToken();

    const { headers: optHeaders, ...restOptions } = options;
    const isFormData = restOptions.body instanceof FormData;
    const config: RequestInit = {
      ...restOptions,
      headers: {
        // Don't set Content-Type for FormData — browser sets it with multipart boundary
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...(token && { Authorization: `Bearer ${token}` }),
        ...optHeaders,
      },
    };

    const method = (options.method || 'GET').toUpperCase();
    const MAX_RETRIES = 3;
    const RETRY_BACKOFF = [1000, 2000, 4000]; // Exponential backoff: 1s, 2s, 4s

    // FIXED: Circuit breaker - DO NOT RETRY 503 errors to prevent connection pool amplification
    // 503 = Service Unavailable typically means connection pool exhaustion
    // Retrying 503s immediately makes the problem worse by adding more connection pressure
    const RETRYABLE_STATUSES = [502, 504, 520]; // Removed 503 from retryable list

    let showedUpdatingMessage = false;
    let updatingToastId: string | number | undefined;

    try {
      let lastError: unknown;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const response = await fetch(url, config);

          if (!response.ok) {
            // Handle 401 Unauthorized - token expired or invalid
            if (response.status === 401) {
              const errorData = await response.json().catch(() => ({}));
              const errorMessage = errorData.error?.message || errorData.error || 'Session expired';

              // Clear local storage and trigger logout callback
              localStorage.removeItem('authToken');
              localStorage.removeItem('authUser');

              if (this.onUnauthorized) {
                this.onUnauthorized();
              }

              // Dismiss updating message if shown
              if (updatingToastId) {
                toast.dismiss(updatingToastId);
              }

              toast.error('Session expired. Please log in again.');
              throw new Error(errorMessage);
            }

            // FIXED: 503 Service Unavailable - DO NOT RETRY (circuit breaker)
            // This is typically connection pool exhaustion, retrying makes it worse
            if (response.status === 503) {
              if (updatingToastId) {
                toast.dismiss(updatingToastId);
              }

              const errorData = await response.json().catch(() => ({}));
              const err = new Error(
                errorData.error?.message ||
                  errorData.error ||
                  'Service temporarily unavailable due to high load',
              );
              (err as any).status = response.status;
              throw err;
            }

            // Handle other transient server errors (502, 504, 520) with retries
            if (RETRYABLE_STATUSES.includes(response.status)) {
              // Show user-friendly "updating" message on first retry
              if (!showedUpdatingMessage && method === 'GET' && attempt < MAX_RETRIES) {
                updatingToastId = toast.loading('System is updating, please wait...', {
                  duration: 10000, // Show for up to 10 seconds
                });
                showedUpdatingMessage = true;
              }

              // Retry GET requests for transient errors
              if (method === 'GET' && attempt < MAX_RETRIES) {
                await new Promise((resolve) => setTimeout(resolve, RETRY_BACKOFF[attempt]));
                continue;
              }
            }

            const errorData = await response.json().catch(() => ({}));
            const err = new Error(
              errorData.error?.message ||
                errorData.error ||
                `HTTP ${response.status}: ${response.statusText}`,
            );
            (err as any).status = response.status;
            throw err;
          }

          // Success - dismiss updating message if shown
          if (updatingToastId) {
            toast.dismiss(updatingToastId);
            if (showedUpdatingMessage && attempt > 0) {
              toast.success('Connection restored!', { duration: 2000 });
            }
          }

          const data: ApiResponse<T> = await response.json();

          if (!data.success && data.error) {
            throw new Error(data.error.message);
          }

          return data.data;
        } catch (err) {
          lastError = err;

          // FIXED: Do not retry 503 errors - they indicate connection pool exhaustion
          const status = (err as any)?.status;
          if (status === 503) {
            throw err; // Immediate failure on 503, no retries
          }

          // Retry network failures on GET requests only (not application-level errors we already threw)
          const isNetworkError = err instanceof TypeError;
          if (method === 'GET' && isNetworkError && attempt < MAX_RETRIES) {
            // Show updating message for network errors too
            if (!showedUpdatingMessage) {
              updatingToastId = toast.loading('Connection issue, retrying...', {
                duration: 10000,
              });
              showedUpdatingMessage = true;
            }

            await new Promise((resolve) => setTimeout(resolve, RETRY_BACKOFF[attempt]));
            continue;
          }

          // Dismiss updating message if this was a fatal error
          if (updatingToastId) {
            toast.dismiss(updatingToastId);
          }

          throw err;
        }
      }

      // Should not be reached, but satisfy TypeScript
      throw lastError;
    } catch (error) {
      // Don't log 404s as errors — they're expected for optional resources (e.g., envelope lookups)
      const errStatus = (error as any)?.status;
      if (errStatus !== 404) {
        console.error(`API Error for ${endpoint}:`, error);
      }

      // Dismiss updating message if there was a fatal error
      if (updatingToastId) {
        toast.dismiss(updatingToastId);
      }

      // Send API errors to Sentry, but suppress expected errors:
      // - 404s: vanity URL lookups the frontend handles gracefully
      // - silent: true requests: the caller has explicitly declared they
      //   will handle (or ignore) the failure. The whole point of the
      //   silent flag is "don't bug the user or operators about this."
      //   Examples: settings.tsx loadReferralStats (403 if user isn't a
      //   DOCTOR/ATTORNEY); listMyEnvelope on cases the user doesn't own.
      // - 401 on /attorney/* endpoints: the attorney magic-link JWT flow
      //   has short-lived tokens by design (via attorneyTokenAuth /
      //   dualAttorneyAuth middleware, which returns 401 "Token expired"
      //   on JWT expiry). A stale link click is normal user behavior, not
      //   an application error; frontend surfaces a friendly re-request
      //   affordance. Clerk-authed 401s are still captured because they
      //   indicate real auth issues.
      // NOTE: 502/503/504 are now reported to Sentry to help diagnose infrastructure issues
      const status = (error as any)?.status;
      const isAttorneyTokenExpired = status === 401 && endpoint.startsWith('/attorney/');
      const isSuppressed =
        requestConfig?.silent === true ||
        (typeof status === 'number' && [404].includes(status)) ||
        isAttorneyTokenExpired;
      if (!isSuppressed) {
        Sentry.withScope((scope) => {
          scope.setTag('api_endpoint', endpoint);
          scope.setTag('request_method', options.method || 'GET');
          scope.setLevel('error');
          Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
        });
      }

      // User-friendly error messages for different error types
      let userMessage: string;
      if ((error as any)?.status === 503) {
        // Specific message for 503 (connection pool issues)
        userMessage = 'Service is at capacity. Please wait a moment before trying again.';
      } else if (this.isTransientError(error)) {
        // For other transient errors (502, 504, network issues)
        userMessage = 'System is temporarily unavailable. Please try again in a moment.';
      } else if ((error as any)?.status === 401) {
        // 401 handled above, don't show duplicate toast
        throw error;
      } else if ((error as any)?.status >= 400 && (error as any)?.status < 500) {
        // Client errors (400-499) - show specific error message
        userMessage = error instanceof Error ? error.message : 'Request failed';
      } else {
        // Server errors or unknown errors
        userMessage = 'An unexpected error occurred. Please try again.';
      }

      // Don't show duplicate toast for 401 errors or silent requests
      if (
        !requestConfig?.silent &&
        !(error instanceof Error && error.message.includes('Session expired'))
      ) {
        toast.error(userMessage);
      }

      throw error;
    }
  }

  // Dashboard endpoints (Clerk auth - user determined from token)
  async getDashboardStats(): Promise<DashboardStats> {
    return this.request<DashboardStats>(`/dashboard`);
  }

  async getPaymentData(): Promise<PaymentData[]> {
    return this.request<PaymentData[]>(`/dashboard/payments`);
  }

  async getRecentRequests(): Promise<RecentRequest[]> {
    return this.request<RecentRequest[]>('/dashboard/recent-requests');
  }

  async getRecentActivity(): Promise<RecentActivity[]> {
    return this.request<RecentActivity[]>(`/dashboard/recent-activity`);
  }

  // Pre-screening requests endpoints (now using intake-requests)
  async getPreScreeningRequests(): Promise<PreScreeningRequest[]> {
    return this.request<PreScreeningRequest[]>(`/intake-requests`);
  }

  async updatePreScreeningRequest(
    id: string,
    status: 'accepted' | 'rejected',
    responseNote: string,
    _attachmentFile?: File,
  ): Promise<PreScreeningRequest> {
    // Convert frontend status to backend format (uppercase)
    const backendStatus = status.toUpperCase();

    return this.request<PreScreeningRequest>(`/intake-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        status: backendStatus,
        responseNote: responseNote || '',
      }),
    });
  }

  // Attorney intake form endpoint
  async submitIntakeForm(
    formData: AttorneyIntakeData,
    doctorVanityUrl: string,
  ): Promise<{ id: string }> {
    // Send encrypted fields directly (attorney-intake.tsx already encrypted them)
    // IMPORTANT: Include encryptedPayload so backend knows encryption is active
    return this.request<{ id: string }>(`/intake-requests/${doctorVanityUrl}`, {
      method: 'POST',
      body: JSON.stringify({
        attorneyName: formData.attorneyName,
        attorneyEmail: formData.attorneyEmail,
        paralegalEmail: formData.paralegalEmail || null, // CC email recipient
        secondaryAttorneyEmail: formData.secondaryAttorneyEmail || null, // CC email recipient
        firmName: formData.firmName || null,
        plaintiff: formData.plaintiffName,
        plaintiffEmail: formData.plaintiffEmail || null, // Smart case matching (optional)
        caseTitle: formData.plaintiffName,
        caseType: formData.caseType, // Use actual case type from form
        specialty: 'Medical Expert',
        urgency: formData.urgency, // Use actual urgency from form
        description: formData.notes,
        notes: formData.notes,
        documentUrl: formData.documentUrl || null,
        documentUrlType: formData.documentUrlType || null,
        // Appointment inquiry fields (already encrypted if encryption enabled)
        appointmentType: formData.appointmentType || null,
        preferredDate: formData.preferredDate || null,
        patientPhone: formData.patientPhone || null,
        patientEmail: formData.patientEmail || null,
        patientDateOfBirth: formData.patientDateOfBirth || null,
        specialInstructions: formData.specialInstructions || null,
        // Include encryptedPayload so backend knows to use encryption adapter correctly
        encryptedPayload: formData.encryptedPayload || null,
      }),
    });
  }

  // Case Import endpoints (Issue #614)
  async importCase(caseData: CaseImportData): Promise<CaseImportResult> {
    return this.request<CaseImportResult>('/cases/import', {
      method: 'POST',
      body: JSON.stringify(caseData),
    });
  }

  async importDeliverable(
    caseId: string,
    deliverableData: DeliverableImportData,
  ): Promise<DeliverableImportResult> {
    return this.request<DeliverableImportResult>(`/cases/${caseId}/deliverables/import`, {
      method: 'POST',
      body: JSON.stringify(deliverableData),
    });
  }

  // Cases endpoints (Clerk auth - user determined from token)
  async getCases(): Promise<CaseRecord[]> {
    return this.request<CaseRecord[]>(`/cases`);
  }

  async getCaseById(id: string): Promise<CaseRecord> {
    return this.request<CaseRecord>(`/cases/${id}`);
  }

  async updateCase(id: string, updates: UpdateCasePayload): Promise<CaseRecord> {
    return this.request<CaseRecord>(`/cases/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async getDeliverables(caseId: string): Promise<Deliverable[]> {
    return this.request<Deliverable[]>(`/cases/${caseId}/deliverables`);
  }

  async updateDeliverableStatus(
    caseId: string,
    deliverableId: string,
    status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED',
  ): Promise<Deliverable> {
    return this.request<Deliverable>(`/cases/${caseId}/deliverables/${deliverableId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  // Doctor Case Notes endpoints - Multiple notes support
  async listDoctorCaseNotes(caseId: string): Promise<
    Array<{
      id: string;
      title: string;
      content: string;
      lastModified: string;
      createdAt: string;
    }>
  > {
    return this.request<
      Array<{
        id: string;
        title: string;
        content: string;
        lastModified: string;
        createdAt: string;
      }>
    >(`/cases/${caseId}/doctor-notes`);
  }

  async getDoctorCaseNote(
    caseId: string,
    noteId: string,
  ): Promise<{
    id: string;
    title: string;
    content: string;
    lastModified: string;
    createdAt: string;
  }> {
    return this.request<{
      id: string;
      title: string;
      content: string;
      lastModified: string;
      createdAt: string;
    }>(`/cases/${caseId}/doctor-notes/${noteId}`);
  }

  async createDoctorCaseNote(
    caseId: string,
    title: string,
    content: string,
  ): Promise<{
    id: string;
    title: string;
    lastModified: string;
    createdAt: string;
  }> {
    return this.request<{
      id: string;
      title: string;
      lastModified: string;
      createdAt: string;
    }>(`/cases/${caseId}/doctor-notes`, {
      method: 'POST',
      body: JSON.stringify({ title, content }),
    });
  }

  async updateDoctorCaseNote(
    caseId: string,
    noteId: string,
    updates: { title?: string; content?: string },
  ): Promise<{
    id: string;
    title: string;
    lastModified: string;
  }> {
    return this.request<{
      id: string;
      title: string;
      lastModified: string;
    }>(`/cases/${caseId}/doctor-notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteDoctorCaseNote(caseId: string, noteId: string): Promise<{ deleted: boolean }> {
    return this.request<{ deleted: boolean }>(`/cases/${caseId}/doctor-notes/${noteId}`, {
      method: 'DELETE',
    });
  }

  async downloadDoctorCaseNote(
    caseId: string,
    noteId: string,
    format: 'pdf' | 'docx' = 'pdf',
  ): Promise<Blob> {
    const response = await fetch(
      `${this.baseUrl}/cases/${caseId}/doctor-notes/${noteId}/download?format=${format}`,
      {
        method: 'GET',
        headers: await this.getHeaders(),
      },
    );

    if (!response.ok) {
      throw new Error('Failed to download note');
    }

    return response.blob();
  }

  // Services endpoints
  async getServices(): Promise<Service[]> {
    return this.request<Service[]>('/services');
  }

  async createService(service: Omit<Service, 'id'>): Promise<Service> {
    return this.request<Service>('/services', {
      method: 'POST',
      body: JSON.stringify(service),
    });
  }

  async updateService(id: string, updates: Partial<Service>): Promise<Service> {
    return this.request<Service>(`/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteService(id: string): Promise<void> {
    return this.request<void>(`/services/${id}`, {
      method: 'DELETE',
    });
  }

  // Invoices endpoints (Clerk auth - user determined from token)
  async getInvoices(status?: string): Promise<Invoice[]> {
    const params = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request<Invoice[]>(`/invoices${params}`);
  }

  async getInvoiceById(id: string): Promise<Invoice> {
    return this.request<Invoice>(`/invoices/${id}`);
  }

  async createInvoice(invoiceData: {
    caseId: string;
    caseName: string;
    clientName: string;
    clientEmail: string;
    services: { serviceId: string; quantity: number; title: string; price: number }[];
    taxRate?: number;
    dueDate?: string;
    notes?: string;
  }): Promise<Invoice> {
    return this.request<Invoice>('/invoices', {
      method: 'POST',
      body: JSON.stringify(invoiceData),
    });
  }

  async updateInvoice(id: string, updates: Partial<Invoice>): Promise<Invoice> {
    return this.request<Invoice>(`/invoices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async updateInvoiceStatus(
    id: string,
    status: 'draft' | 'sent' | 'paid' | 'overdue',
  ): Promise<Invoice> {
    return this.request<Invoice>(`/invoices/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async sendInvoice(id: string): Promise<{ sent: boolean; emailSent: boolean; sentTo: string }> {
    return this.request<{ sent: boolean; emailSent: boolean; sentTo: string }>(
      `/invoices/${id}/send`,
      {
        method: 'POST',
      },
    );
  }

  async sendInvoiceReminder(
    id: string,
  ): Promise<{ sent: boolean; emailSent: boolean; sentTo: string }> {
    return this.request<{ sent: boolean; emailSent: boolean; sentTo: string }>(
      `/invoices/${id}/send-reminder`,
      {
        method: 'POST',
      },
    );
  }

  async generateInvoicePDF(id: string): Promise<{ downloadUrl: string; fileName: string }> {
    return this.request<{ downloadUrl: string; fileName: string }>(`/invoices/${id}/pdf`);
  }

  async deleteInvoice(id: string): Promise<void> {
    return this.request<void>(`/invoices/${id}`, {
      method: 'DELETE',
    });
  }

  // Payment endpoints
  async recordPayment(
    invoiceId: string,
    paymentData: RecordPaymentRequest,
  ): Promise<RecordPaymentResult> {
    return this.request<RecordPaymentResult>(`/invoices/${invoiceId}/payments`, {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  async getPaymentHistory(invoiceId: string): Promise<Payment[]> {
    const response = await this.request<{ payments: Payment[] }>(`/invoices/${invoiceId}/payments`);
    return response.payments;
  }

  async updateCheckStatus(
    paymentId: string,
    statusData: UpdateCheckStatusRequest,
  ): Promise<Payment> {
    const response = await this.request<{ payment: Payment }>(
      `/invoices/payments/${paymentId}/check-status`,
      {
        method: 'PATCH',
        body: JSON.stringify(statusData),
      },
    );
    return response.payment;
  }

  // Auth profile endpoints
  async getAuthProfile(): Promise<ProfileSettings> {
    return this.request<ProfileSettings>('/auth/profile');
  }

  async updateAuthProfile(updates: Partial<ProfileSettings>): Promise<ProfileSettings> {
    return this.request<ProfileSettings>('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // Settings endpoints
  async getProfile(): Promise<ProfileSettings> {
    return this.request<ProfileSettings>('/settings/profile');
  }

  async updateProfile(updates: Partial<ProfileSettings>): Promise<ProfileSettings> {
    return this.request<ProfileSettings>('/settings/profile', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async getPublicProfile(vanityUrl: string): Promise<PublicDoctorProfile> {
    return this.request<PublicDoctorProfile>(`/intake-requests/doctor/${vanityUrl}`);
  }

  async getVanityUrlSettings(): Promise<{
    vanityUrl: string;
    vanityUrlActive: boolean;
    fullUrl: string;
    intakeFormUrl: string;
  }> {
    return this.request('/settings/vanity-url');
  }

  async updateVanityUrlSettings(
    vanityUrl: string,
    vanityUrlActive: boolean,
    userData?: { name?: string; title?: string; specialty?: string },
  ): Promise<{
    vanityUrl: string;
    vanityUrlActive: boolean;
    fullUrl: string;
    intakeFormUrl: string;
  }> {
    return this.request('/settings/vanity-url', {
      method: 'PUT',
      body: JSON.stringify({
        vanityUrl,
        vanityUrlActive,
        ...(userData || {}),
      }),
    });
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<{
    passwordChanged: boolean;
    passwordLastChanged: string;
  }> {
    return this.request('/settings/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
    });
  }

  // File upload endpoint
  async uploadFile(file: File, caseId: string, category?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caseId', caseId);
    if (category) formData.append('category', category);

    return this.request<any>('/files/upload', {
      method: 'POST',
      body: formData,
    });
  }

  // Get file download URL
  async getFileDownloadUrl(
    fileId: string,
  ): Promise<{ downloadUrl: string; fileName: string; expiresIn: number }> {
    return this.request<{ downloadUrl: string; fileName: string; expiresIn: number }>(
      `/files/${fileId}/download`,
    );
  }

  // Delete file
  async deleteFile(fileId: string): Promise<void> {
    return this.request<void>(`/files/${fileId}`, {
      method: 'DELETE',
    });
  }

  // Get pending intake requests count
  async getPendingRequestsCount(
    doctorVanityUrl?: string,
  ): Promise<{ count: number; timestamp: string }> {
    const params = new URLSearchParams();
    if (doctorVanityUrl) {
      params.append('doctor', doctorVanityUrl);
    }

    const url = `/intake-requests/pending-count${params.toString() ? `?${params.toString()}` : ''}`;
    return this.request<{ count: number; timestamp: string }>(url, {
      method: 'GET',
    });
  }

  // Health check endpoint
  async checkHealth(): Promise<{ status: string; timestamp: string }> {
    return this.request<{ status: string; timestamp: string }>('/health', {
      // Remove base URL prefix since health is at root level
      method: 'GET',
    });
  }

  // Stripe Connect endpoints
  async getStripeAccountStatus(): Promise<StripeAccountStatus> {
    return this.request<StripeAccountStatus>('/stripe/connect/account');
  }

  async createStripeAccount(): Promise<StripeAccountCreated> {
    return this.request<StripeAccountCreated>('/stripe/connect/account', {
      method: 'POST',
    });
  }

  async deleteStripeAccount(): Promise<{ deleted: boolean }> {
    return this.request<{ deleted: boolean }>('/stripe/connect/account', {
      method: 'DELETE',
    });
  }

  async createStripeOnboardingLink(
    refreshUrl: string,
    returnUrl: string,
  ): Promise<StripeOnboardingLink> {
    return this.request<StripeOnboardingLink>('/stripe/connect/onboard', {
      method: 'POST',
      body: JSON.stringify({ refreshUrl, returnUrl }),
    });
  }

  /**
   * Ensure a Connect account exists, then return a fresh onboarding link.
   *
   * `POST /stripe/connect/account` answers 409 ACCOUNT_EXISTS when the
   * recorded account is still reachable in the platform's current Stripe
   * mode. That is not a failure for this flow: the caller wants an
   * onboarding link for whatever account the user has, so the conflict is
   * swallowed and the link is requested against the existing account.
   *
   * The creation call is `silent` because that expected conflict would
   * otherwise toast "Payment account already exists" and report a Sentry
   * error on every normal reconnect. Genuine creation failures are still
   * reported here and rethrown for the caller to surface.
   */
  async ensureStripeOnboardingLink(
    refreshUrl: string,
    returnUrl: string,
  ): Promise<StripeOnboardingLink> {
    try {
      await this.request<StripeAccountCreated>(
        '/stripe/connect/account',
        { method: 'POST' },
        { silent: true },
      );
    } catch (error) {
      const status = (error as { status?: number })?.status;
      const message = error instanceof Error ? error.message : '';
      if (status !== 409 && !/already exists/i.test(message)) {
        Sentry.withScope((scope) => {
          scope.setTag('api_endpoint', '/stripe/connect/account');
          scope.setTag('request_method', 'POST');
          scope.setLevel('error');
          Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
        });
        throw error;
      }
    }

    return this.createStripeOnboardingLink(refreshUrl, returnUrl);
  }

  async createStripeDashboardLink(): Promise<StripeDashboardLink> {
    return this.request<StripeDashboardLink>('/stripe/connect/dashboard', {
      method: 'POST',
    });
  }

  async getStripeBalance(): Promise<StripeBalance> {
    return this.request<StripeBalance>('/stripe/connect/balance');
  }

  async getStripePayouts(limit: number = 10): Promise<StripePayout[]> {
    return this.request<StripePayout[]>(`/stripe/connect/payouts?limit=${limit}`);
  }

  // Encryption Management
  async getEncryptionStatus(): Promise<EncryptionStatus> {
    return this.request<EncryptionStatus>('/users/encryption/status');
  }

  async initializeEncryption(
    publicKey?: string,
    encryptedPrivateKey?: string | null,
  ): Promise<EncryptionInitResponse> {
    return this.request<EncryptionInitResponse>('/users/encryption/initialize', {
      method: 'POST',
      body: JSON.stringify({
        publicKey,
        encryptedPrivateKey,
      }),
    });
  }

  async updateEncryptedPrivateKey(encryptedPrivateKey: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/users/encryption/private-key', {
      method: 'PUT',
      body: JSON.stringify({ encryptedPrivateKey }),
    });
  }

  async getEncryptionSalt(): Promise<EncryptionSaltResponse> {
    return this.request<EncryptionSaltResponse>('/users/encryption/salt');
  }

  async getDoctorPublicKey(
    vanityUrl: string,
  ): Promise<{ encryptionEnabled: boolean; publicKey: string | null }> {
    return this.request<{ encryptionEnabled: boolean; publicKey: string | null }>(
      `/intake-requests/doctor/${vanityUrl}/public-key`,
    );
  }

  // ==========================================
  // ENVELOPE ENCRYPTION - Case Key Envelopes
  // ==========================================

  /** Create or update an envelope (wrapped case key) for a participant */
  async createCaseEnvelope(
    caseId: string,
    userId: string,
    encryptedCaseKey: string,
  ): Promise<{
    id: string;
    caseId: string;
    userId: string;
    algorithm: string;
    createdAt: string;
    updatedAt: string;
  }> {
    return this.request(`/cases/${caseId}/envelopes`, {
      method: 'POST',
      body: JSON.stringify({ userId, encryptedCaseKey }),
    });
  }

  /** Get the current user's envelope for a case (includes wrapped key) */
  async getMyCaseEnvelope(caseId: string): Promise<{
    id: string;
    caseId: string;
    userId: string;
    encryptedCaseKey: string;
    algorithm: string;
    createdAt: string;
    updatedAt: string;
  } | null> {
    try {
      return await this.request(`/cases/${caseId}/envelopes/me`, {}, { silent: true });
    } catch (error: any) {
      if (error?.status === 404) return null;
      throw error;
    }
  }

  /** List all envelopes for a case (case owner only) */
  async listCaseEnvelopes(caseId: string): Promise<
    Array<{
      id: string;
      userId: string;
      algorithm: string;
      createdAt: string;
      updatedAt: string;
      user: { id: string; role: string };
    }>
  > {
    return this.request(`/cases/${caseId}/envelopes`);
  }

  /** Revoke a user's envelope access (case owner only) */
  async deleteCaseEnvelope(caseId: string, userId: string): Promise<{ revoked: boolean }> {
    return this.request(`/cases/${caseId}/envelopes/${userId}`, {
      method: 'DELETE',
    });
  }

  /** Get a user's public key by their user ID (for wrapping case keys) */
  async getUserPublicKey(
    userId: string,
    config?: { silent?: boolean },
  ): Promise<{ publicKey: string | null }> {
    return this.request(`/users/${userId}/public-key`, {}, config);
  }

  // ==========================================
  // ENVELOPE RE-ENCRYPTION
  // ==========================================

  /** Re-encrypt case fields from RSA → envelope encryption (doctor only) */
  async reEncryptCase(
    caseId: string,
    fields: Record<string, string>,
  ): Promise<{
    id: string;
    encryptionVersion: number;
    updatedAt: string;
    fieldsUpdated: number;
    alreadyMigrated?: boolean;
  }> {
    return this.request(`/cases/${caseId}/re-encrypt`, {
      method: 'POST',
      body: JSON.stringify({ fields }),
    });
  }

  /** Re-encrypt intake request fields from RSA → envelope encryption (doctor only) */
  async reEncryptIntakeRequest(
    id: string,
    caseId: string,
    fields: Record<string, string>,
  ): Promise<{
    id: string;
    fieldsUpdated: number;
    fieldsSkipped: number;
    alreadyMigrated?: boolean;
  }> {
    return this.request(`/intake-requests/${id}/re-encrypt`, {
      method: 'POST',
      body: JSON.stringify({ fields, caseId }),
    });
  }

  /** Send a templated email from doctor to attorney on a case.
   *
   * Pass `recipientEmail` when the caller has decrypted the attorney's email
   * client-side (V2 envelope-encrypted cases store the address as a
   * `CASEKEY:` blob the server cannot decrypt). When omitted, the backend
   * falls back to plaintext DB fields.
   */
  async sendCaseTemplateEmail(
    caseId: string,
    templateType: string,
    options?: { customSubject?: string; customBody?: string; recipientEmail?: string },
  ): Promise<{ sent: boolean; sentTo: string }> {
    return this.request<{ sent: boolean; sentTo: string }>(`/cases/${caseId}/send-template-email`, {
      method: 'POST',
      body: JSON.stringify({
        templateType,
        ...(options?.customSubject && { customSubject: options.customSubject }),
        ...(options?.customBody && { customBody: options.customBody }),
        ...(options?.recipientEmail && { recipientEmail: options.recipientEmail }),
      }),
    });
  }

  /** Find a user by email (for granting attorney envelope access) */
  async findUserByEmail(
    email: string,
    config?: { silent?: boolean },
  ): Promise<{ id: string; hasPublicKey: boolean } | null> {
    try {
      return await this.request(`/users/by-email?email=${encodeURIComponent(email)}`, {}, config);
    } catch (error: any) {
      if (error?.status === 404) return null;
      throw error;
    }
  }

  // ==========================================
  // ADMIN ENDPOINTS - Platform Discount Management
  // ==========================================

  /**
   * Get all doctors with their platform fee discount information
   * @returns List of all doctors with discount data
   */
  async getAllDoctors(): Promise<AdminDoctor[]> {
    const response = await this.request<{ doctors: AdminDoctor[] }>('/admin/doctors');
    return response.doctors;
  }

  /**
   * Update a doctor's platform fee discount
   * @param doctorId Doctor's database ID
   * @param discount Discount percentage (0-100)
   * @returns Updated doctor information
   */
  async updateDoctorDiscount(doctorId: string, discount: number): Promise<AdminDoctor> {
    const response = await this.request<{ doctor: AdminDoctor }>(
      `/admin/doctors/${doctorId}/discount`,
      {
        method: 'PATCH',
        body: JSON.stringify({ discount }),
      },
    );
    return response.doctor;
  }

  /**
   * Grant / change a doctor's $150/mo subscription discount.
   * @param doctorId Doctor's database ID
   * @param percentOff Percent-off recurring (integer 1-99)
   * @returns Grant result (couponId, subscription id, previous percent-off)
   */
  async grantDoctorSubscriptionDiscount(
    doctorId: string,
    percentOff: number,
  ): Promise<{
    doctorId: string;
    percentOff: number;
    couponId: string;
    subscriptionId: string;
    previousPercentOff: number | null;
  }> {
    return this.request<{
      doctorId: string;
      percentOff: number;
      couponId: string;
      subscriptionId: string;
      previousPercentOff: number | null;
    }>(`/admin/doctors/${doctorId}/subscription-discount`, {
      method: 'POST',
      body: JSON.stringify({ percentOff }),
    });
  }

  /**
   * Revoke a doctor's $150/mo subscription discount. Idempotent — a
   * doctor with no active discount returns `{ revoked: false }`.
   * @param doctorId Doctor's database ID
   */
  async revokeDoctorSubscriptionDiscount(doctorId: string): Promise<{
    doctorId: string;
    revoked: boolean;
    previousPercentOff: number | null;
  }> {
    return this.request<{
      doctorId: string;
      revoked: boolean;
      previousPercentOff: number | null;
    }>(`/admin/doctors/${doctorId}/subscription-discount`, {
      method: 'DELETE',
    });
  }

  /**
   * Reset a user's encryption state (admin support workflow for
   * forgotten passphrases). Attorneys only in v1.
   *
   * @param userId Target user's database ID
   * @param reason Optional operator note for the audit trail
   */
  async resetUserEncryption(
    userId: string,
    reason?: string,
  ): Promise<{
    target: { id: string; email: string; role: string; encryptionEnabled: boolean };
    envelopesDeleted: number;
    auditId: string;
    performedAt: string;
    noop?: boolean;
    message?: string;
  }> {
    return await this.request(`/admin/users/${userId}/reset-encryption`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    });
  }

  /**
   * Get production metrics for admin dashboard
   * @returns Production metrics including users, revenue, and time-series data
   */
  async getProductionMetrics(): Promise<ProductionMetrics> {
    const response = await this.request<ProductionMetrics>('/admin/metrics/production');
    return response;
  }

  /**
   * Get environment health status for all environments
   * @returns Health status for localhost, staging, and production
   */
  async getEnvironmentHealth(): Promise<EnvironmentHealth> {
    const response = await this.request<EnvironmentHealth>('/admin/health/environments');
    return response;
  }

  // ==========================================
  // System Settings (Staging Access)
  // ==========================================

  /**
   * Check if staging password protection is enabled (public endpoint)
   */
  async checkStagingAccess(): Promise<{ enabled: boolean }> {
    const response = await this.request<{ enabled: boolean }>('/system-settings/staging-access');
    return response;
  }

  /**
   * Verify staging password (public endpoint)
   */
  async verifyStagingPassword(password: string): Promise<{ valid: boolean }> {
    const response = await this.request<{ valid: boolean }>(
      '/system-settings/staging-access/verify',
      {
        method: 'POST',
        body: JSON.stringify({ password }),
      },
    );
    return response;
  }

  /**
   * Update staging access settings (admin only)
   */
  async updateStagingAccess(
    enabled: boolean,
    password?: string,
  ): Promise<{ enabled: boolean; message: string }> {
    const response = await this.request<{ enabled: boolean; message: string }>(
      '/system-settings/staging-access',
      {
        method: 'PUT',
        body: JSON.stringify({ enabled, password }),
      },
    );
    return response;
  }

  /**
   * Get staging access status (admin only)
   */
  async getStagingAccessStatus(): Promise<{ enabled: boolean }> {
    const response = await this.request<{ enabled: boolean }>(
      '/system-settings/staging-access/status',
    );
    return response;
  }

  // ==========================================
  // Notifications
  // ==========================================

  async getNotifications(): Promise<DashboardNotification[]> {
    return this.request<DashboardNotification[]>('/dashboard/notifications', {}, { silent: true });
  }

  async markNotificationRead(id: string): Promise<void> {
    await this.request(`/dashboard/notifications/${id}/read`, { method: 'PATCH' });
  }

  async markAllNotificationsRead(): Promise<void> {
    await this.request('/dashboard/notifications/read-all', { method: 'PATCH' });
  }

  // ==========================================
  // Invitations (Client Email Invites)
  // ==========================================

  /**
   * Get list of unique attorneys from doctor's cases (server-side decrypted)
   */
  async getInvitationAttorneys(): Promise<{ email: string; name: string; firmName: string }[]> {
    const response =
      await this.request<{ email: string; name: string; firmName: string }[]>(
        '/invitations/attorneys',
      );
    return response;
  }

  /**
   * Send invitation emails to selected attorneys
   */
  async sendInvitationEmails(data: {
    recipients: string[];
    subject: string;
    body: string;
  }): Promise<{ sent: number; messageId?: string }> {
    const response = await this.request<{ sent: number; messageId?: string }>('/invitations/send', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  }

  // ==========================================
  // Attorney Clerk-Authenticated Endpoints
  // ==========================================

  /**
   * Get attorney's cases using Clerk auth (no JWT token needed)
   */
  async getAttorneyCasesClerk(): Promise<any> {
    return this.request('/attorney/cases');
  }

  async attorneyImportCase(caseData: CaseImportData): Promise<CaseImportResult> {
    return this.request<CaseImportResult>('/attorney/cases/import', {
      method: 'POST',
      body: JSON.stringify(caseData),
    });
  }

  async attorneyImportDeliverable(
    caseId: string,
    deliverableData: DeliverableImportData,
  ): Promise<DeliverableImportResult> {
    return this.request<DeliverableImportResult>(`/attorney/cases/${caseId}/deliverables/import`, {
      method: 'POST',
      body: JSON.stringify(deliverableData),
    });
  }

  /**
   * Get a specific case by ID using Clerk auth
   */
  async getAttorneyCaseByIdClerk(caseId: string): Promise<any> {
    return this.request(`/attorney/cases/${encodeURIComponent(caseId)}`);
  }

  /**
   * Verify cases associated with the attorney's Clerk account email
   */
  async linkAttorneyCases(): Promise<{ caseCount: number; attorneyEmail: string }> {
    return this.request<{ caseCount: number; attorneyEmail: string }>('/attorney/link-cases', {
      method: 'POST',
    });
  }

  // ==========================================
  // Attorney Dashboard & Analytics
  // ==========================================

  async getAttorneyDashboardStats(): Promise<AttorneyDashboardStats> {
    return this.request<AttorneyDashboardStats>('/attorney/dashboard');
  }

  async getAttorneyPaymentData(): Promise<PaymentData[]> {
    return this.request<PaymentData[]>('/attorney/dashboard/payments');
  }

  async getAttorneyRecentActivity(limit?: number): Promise<AttorneyActivity[]> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request<AttorneyActivity[]>(`/attorney/dashboard/activity${params}`);
  }

  // ==========================================
  // Attorney Invoices
  // ==========================================

  async getAttorneyInvoices(status?: string): Promise<AttorneyInvoicesResponse> {
    const params = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
    return this.request<AttorneyInvoicesResponse>(`/attorney/invoices${params}`);
  }

  async deleteAttorneyInvoice(invoiceId: string): Promise<{ id: string }> {
    return this.request<{ id: string }>(`/attorney/invoices/${encodeURIComponent(invoiceId)}`, {
      method: 'DELETE',
    });
  }

  async updateAttorneyInvoice(
    invoiceId: string,
    data: {
      notes?: string;
      title?: string;
      description?: string;
      billableItems?: any[];
      dueDate?: string;
    },
  ): Promise<any> {
    return this.request(`/attorney/invoices/${encodeURIComponent(invoiceId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ==========================================
  // Attorney Case Update & Notes
  // ==========================================

  async updateAttorneyCase(
    caseId: string,
    data: {
      title?: string;
      description?: string;
      status?: string;
      specialty?: string;
      injuryDate?: string | null;
      courtDate?: string | null;
      jurisdiction?: string;
    },
  ): Promise<any> {
    return this.request(`/attorney/cases/${encodeURIComponent(caseId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createAttorneyCaseNote(
    caseId: string,
    data: { content: string; visibility: string },
  ): Promise<any> {
    return this.request(`/attorney/cases/${encodeURIComponent(caseId)}/notes`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAttorneyCaseNote(
    caseId: string,
    noteId: string,
    data: { content?: string; visibility?: string },
  ): Promise<any> {
    return this.request(
      `/attorney/cases/${encodeURIComponent(caseId)}/notes/${encodeURIComponent(noteId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    );
  }

  async deleteAttorneyCaseNote(caseId: string, noteId: string): Promise<{ id: string }> {
    return this.request<{ id: string }>(
      `/attorney/cases/${encodeURIComponent(caseId)}/notes/${encodeURIComponent(noteId)}`,
      {
        method: 'DELETE',
      },
    );
  }

  // ==========================================
  // Attorney File Upload & Deliverables
  // ==========================================

  async uploadAttorneyFile(caseId: string, file: File, category?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (category) formData.append('category', category);

    return this.request<any>(`/attorney/cases/${encodeURIComponent(caseId)}/files`, {
      method: 'POST',
      body: formData,
    });
  }

  async searchExperts(query?: string, specialty?: string): Promise<ExpertSearchResult[]> {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (specialty) params.set('specialty', specialty);
    const qs = params.toString();
    const result = await this.request<{ data: ExpertSearchResult[] }>(
      `/attorney/experts${qs ? `?${qs}` : ''}`,
    );
    return (result as any).data || (result as any);
  }

  async requestDeliverable(
    caseId: string,
    data: DeliverableRequestData,
  ): Promise<{ deliverableId: string }> {
    return this.request<{ deliverableId: string }>(
      `/attorney/cases/${encodeURIComponent(caseId)}/deliverables`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  async cancelDeliverable(
    caseId: string,
    deliverableId: string,
  ): Promise<{ id: string; status: string }> {
    return this.request<{ id: string; status: string }>(
      `/attorney/cases/${encodeURIComponent(caseId)}/deliverables/${encodeURIComponent(deliverableId)}/cancel`,
      { method: 'PATCH' },
    );
  }

  // ==========================================
  // Case Collaborators
  // ==========================================

  async getCaseCollaborators(caseId: string): Promise<CaseCollaborator[]> {
    return this.request<CaseCollaborator[]>(
      `/attorney/cases/${encodeURIComponent(caseId)}/collaborators`,
    );
  }

  async addCaseCollaborator(
    caseId: string,
    email: string,
    role: string,
  ): Promise<CaseCollaborator> {
    return this.request<CaseCollaborator>(
      `/attorney/cases/${encodeURIComponent(caseId)}/collaborators`,
      {
        method: 'POST',
        body: JSON.stringify({ email, role }),
      },
    );
  }

  async removeCaseCollaborator(caseId: string, collaboratorId: string): Promise<void> {
    await this.request(
      `/attorney/cases/${encodeURIComponent(caseId)}/collaborators/${encodeURIComponent(collaboratorId)}`,
      {
        method: 'DELETE',
      },
    );
  }

  // ==========================================
  // Attorney Notifications
  // ==========================================

  async getAttorneyNotifications(): Promise<DashboardNotification[]> {
    return this.request<DashboardNotification[]>('/attorney/notifications', {}, { silent: true });
  }

  async markAttorneyNotificationRead(id: string): Promise<void> {
    await this.request(`/attorney/notifications/${encodeURIComponent(id)}/read`, {
      method: 'PATCH',
    });
  }

  async markAllAttorneyNotificationsRead(): Promise<void> {
    await this.request('/attorney/notifications/read-all', {
      method: 'POST',
    });
  }

  // ==========================================
  // Attorney Profile
  // ==========================================

  async getAttorneyProfile(): Promise<AttorneyProfile> {
    return this.request<AttorneyProfile>('/attorney/profile');
  }

  async updateAttorneyProfile(data: Partial<AttorneyProfile>): Promise<AttorneyProfile> {
    return this.request<AttorneyProfile>('/attorney/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ============================================
  // Attorney Client Intake (Public)
  // ============================================

  async getAttorneyProfileForIntake(vanityUrl: string): Promise<AttorneyPublicProfile> {
    return this.request<AttorneyPublicProfile>(
      `/intake-requests/client-intake/${encodeURIComponent(vanityUrl)}`,
    );
  }

  async getAttorneyPublicKeyForIntake(
    vanityUrl: string,
  ): Promise<{ encryptionEnabled: boolean; publicKey: string | null }> {
    return this.request<{ encryptionEnabled: boolean; publicKey: string | null }>(
      `/intake-requests/client-intake/${encodeURIComponent(vanityUrl)}/public-key`,
    );
  }

  async submitAttorneyClientIntake(
    vanityUrl: string,
    data: ClientIntakeData,
  ): Promise<{ id: string }> {
    return this.request<{ id: string }>(
      `/intake-requests/client-intake/${encodeURIComponent(vanityUrl)}`,
      {
        method: 'POST',
        body: JSON.stringify({
          clientName: data.clientName,
          clientEmail: data.clientEmail,
          clientPhone: data.clientPhone || null,
          patientDateOfBirth: data.patientDateOfBirth || null,
          caseType: data.caseType,
          description: data.description,
          urgency: data.urgency || 'MEDIUM',
          dateNeeded: data.dateNeeded || null,
          notes: data.notes || null,
          encryptedPayload: data.encryptedPayload || null,
        }),
      },
    );
  }

  // ============================================
  // Attorney Intake Request Management
  // ============================================

  async getAttorneyIntakeRequests(status?: string): Promise<AttorneyIntakeRequestsResponse> {
    const params = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
    return this.request<AttorneyIntakeRequestsResponse>(`/attorney/intake-requests${params}`);
  }

  async updateAttorneyIntakeRequest(
    id: string,
    data: { status: string; responseNote?: string },
  ): Promise<{ id: string; status: string; caseId: string | null }> {
    return this.request<{ id: string; status: string; caseId: string | null }>(
      `/attorney/intake-requests/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  }

  // ============================================
  // Team Member Management
  // ============================================

  async getTeamMembers(): Promise<{ members: TeamMember[] }> {
    return this.request<{ members: TeamMember[] }>('/team/members');
  }

  async inviteTeamMember(email: string, role: 'ADMIN' | 'MEMBER'): Promise<TeamMember> {
    return this.request<TeamMember>('/team/invite', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  }

  async updateTeamMemberRole(
    id: string,
    role: 'ADMIN' | 'MEMBER',
  ): Promise<{ id: string; role: string }> {
    return this.request<{ id: string; role: string }>(
      `/team/members/${encodeURIComponent(id)}/role`,
      {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      },
    );
  }

  async removeTeamMember(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/team/members/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async acceptTeamInvite(): Promise<{
    id: string;
    teamOwnerId: string;
    role: string;
    status: string;
  }> {
    return this.request<{ id: string; teamOwnerId: string; role: string; status: string }>(
      '/team/accept-invite',
      {
        method: 'POST',
      },
    );
  }

  async getMyTeam(): Promise<TeamInfo> {
    return this.request<TeamInfo>('/team/my-team');
  }

  async getTeamInviteDetails(
    inviteId: string,
  ): Promise<{ doctorName: string; email: string; role: string }> {
    const url = `${this.baseUrl}/team/invite-details/${encodeURIComponent(inviteId)}`;
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (_networkError) {
      throw new Error('Network error while fetching invitation details');
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.error?.message || 'Invitation not found');
    }
    const data = await response.json();
    return data.data;
  }

  // ============================================
  // Referral Program
  // ============================================

  async getReferralStats(config?: { silent?: boolean }): Promise<ReferralStats> {
    return this.request<ReferralStats>('/referrals/stats', {}, config);
  }

  async validateReferralCode(code: string): Promise<{ valid: boolean }> {
    return this.request<{ valid: boolean }>('/referrals/validate', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async getMyReferralCode(): Promise<{ referralCode: string }> {
    return this.request<{ referralCode: string }>('/referrals/my-code');
  }

  // ============================================
  // Vanity URL
  // ============================================

  async checkVanityUrl(slug: string): Promise<{ available: boolean; currentOwner?: boolean }> {
    return this.request<{ available: boolean; currentOwner?: boolean }>(
      `/auth/check-vanity-url/${encodeURIComponent(slug)}`,
    );
  }

  // ============================================
  // Attorney Subscriptions
  // ============================================

  async createSubscriptionCheckout(tier: string): Promise<{ sessionId: string; url: string }> {
    return this.request<{ sessionId: string; url: string }>('/subscriptions/checkout', {
      method: 'POST',
      body: JSON.stringify({ tier }),
    });
  }

  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    return this.request<SubscriptionStatus>('/subscriptions/status');
  }

  /** Switch an active/trialing subscription between the flat $150/mo plan
   * and the 3%-of-revenue plan, in place on the same card on file. */
  async switchSubscriptionPlan(tier: string): Promise<{ tier: string; subscriptionId: string }> {
    return this.request<{ tier: string; subscriptionId: string }>('/subscriptions/switch', {
      method: 'POST',
      body: JSON.stringify({ tier }),
    });
  }

  async createBillingPortal(): Promise<{ url: string }> {
    return this.request<{ url: string }>('/subscriptions/portal', {
      method: 'POST',
    });
  }

  // ============================================
  // Calendar Integration
  // ============================================

  async getCalendarConnection(config?: { silent?: boolean }): Promise<CalendarConnectionState> {
    return this.request<CalendarConnectionState>('/calendar/connection', {}, config);
  }

  /** Creates the subscription feed if it doesn't exist yet (idempotent). */
  async createCalendarConnection(timezone?: string): Promise<CalendarConnectionState> {
    return this.request<CalendarConnectionState>('/calendar/connection', {
      method: 'POST',
      body: JSON.stringify({
        timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    });
  }

  /** Rotates the feed token — the previous URL stops working immediately. */
  async regenerateCalendarFeed(): Promise<CalendarConnectionState> {
    return this.request<CalendarConnectionState>('/calendar/connection/regenerate', {
      method: 'POST',
    });
  }

  async disconnectCalendar(): Promise<{ connected: boolean }> {
    return this.request<{ connected: boolean }>('/calendar/connection', { method: 'DELETE' });
  }

  async updateCalendarPreferences(
    updates: Partial<CalendarPreferences>,
  ): Promise<{ preferences: CalendarPreferences }> {
    return this.request<{ preferences: CalendarPreferences }>('/calendar/connection/preferences', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async getCalendarEvents(config?: { silent?: boolean }): Promise<CalendarEventSummary[]> {
    return this.request<CalendarEventSummary[]>('/calendar/events', {}, config);
  }

  /**
   * Downloads a single event as `.ics`. Returns a blob URL the caller must
   * revoke; the endpoint is auth-guarded so a plain `<a href>` won't work.
   */
  async downloadCalendarEventIcs(entityType: CalendarEntityType, entityId: string): Promise<Blob> {
    const token = await this.getToken();
    const response = await fetch(
      `${this.baseUrl}/calendar/events/${entityType}/${encodeURIComponent(entityId)}.ics`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) {
      throw new Error('Failed to download calendar file');
    }
    return response.blob();
  }

  /** Sync subscription state from Stripe checkout session (called on return from Stripe). */
  async syncSubscriptionFromCheckout(
    sessionId: string,
  ): Promise<{ synced: boolean; reason?: string }> {
    return this.request<{ synced: boolean; reason?: string }>('/subscriptions/sync', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }
}

// Create and export the API client instance
export const apiClient = new ApiClient(API_BASE_URL);

// Export types for use in components
export type {
  AdminDoctor,
  AttorneyActivity,
  AttorneyDashboardStats,
  AttorneyIntakeData,
  AttorneyIntakeRequest,
  AttorneyIntakeRequestsResponse,
  AttorneyInvoice,
  AttorneyInvoicesResponse,
  AttorneyProfile,
  AttorneyPublicProfile,
  CalendarConnectionState,
  CalendarEntityType,
  CalendarEventSummary,
  CalendarPreferences,
  CaseImportData,
  CaseImportResult,
  CaseRecord,
  ClientIntakeData,
  DashboardStats,
  Deliverable,
  DeliverableImportData,
  DeliverableImportResult,
  DeliverableRequestData,
  EncryptionInitResponse,
  EncryptionSaltResponse,
  EnvironmentHealth,
  EnvironmentHealthStatus,
  ExpertSearchResult,
  Invoice,
  Payment,
  PaymentData,
  PreScreeningRequest,
  ProductionMetrics,
  ProfileSettings,
  RecentActivity,
  RecentRequest,
  RecordPaymentRequest,
  RecordPaymentResult,
  Service,
  StripeAccountCreated,
  StripeAccountStatus,
  StripeBalance,
  StripeDashboardLink,
  StripeOnboardingLink,
  StripePayout,
  UpdateCheckStatusRequest,
};

export { CheckStatus, PaymentMethod, PaymentStatus };
